import { RefObject, useEffect, useState } from 'react';

interface UseInViewOptions {
  /**
   * Overscan around the root. Percentages resolve against the root's *own* size,
   * which is what lets one value serve differently-sized scroll containers
   * without any row-height maths.
   */
  rootMargin?: string;
  /**
   * Don't wait to be seen — latch as soon as `enabled` allows it. For content that
   * must load immediately. Does not outrank `enabled`; that gate always holds.
   */
  eager?: boolean;
  /**
   * Scroll container to observe against; omit for the viewport.
   *
   * `'nearest-scrollable'` finds it by walking up from the element, which is
   * usually what you want: which ancestor actually scrolls is a layout fact, and
   * a hand-passed ref encodes an assumption that silently rots. The Libraries
   * page is the cautionary tale — its content sits inside a `GradientScrollDiv`
   * whose inner div declares `overflow-y: scroll`, so passing that ref looks
   * obviously right, but the page's real scroller is an `AppPage` wrapper further
   * up and the inner div grows to full content height. The observer root then
   * covered every card at once and the deferral silently did nothing.
   *
   * A `RefObject` is read at effect time, so a ref filled during the same commit
   * as the observed element is already populated — refs attach before effects
   * run. Either form falls back to the viewport when it resolves to nothing,
   * which over-fetches rather than never fetching.
   *
   * ⚠️ Only the ref identity is a dependency, never `.current`. Swapping either
   * this node or the observed one for a *different* node while the latch is still
   * open leaves the observer watching the old node, and it never fires. No current
   * caller does that — they mount and unmount rather than re-point — but a caller
   * that reuses one ref across two elements would need to force a remount instead.
   */
  root?: RefObject<Element | null> | 'nearest-scrollable';
  /**
   * While false nothing is observed and the latch cannot flip. Suppresses new
   * loads only — an already-latched element stays latched, so toggling this off
   * never unloads anything.
   *
   * Needed wherever a container hides its content without clipping it: an
   * observer rooted on a scroll container measures against that container, so
   * rows report as intersecting even while the whole thing is transformed
   * off-screen or `visibility: hidden` (both of which IntersectionObserver
   * ignores). See `LibrarySwitchRow`.
   */
  enabled?: boolean;
}

/**
 * The nearest ancestor that actually scrolls.
 *
 * `scrollHeight > clientHeight` is load-bearing, not a micro-optimisation: an element can
 * declare `overflow-y: scroll` and still not be a scroller, because its own height grew to
 * fit its content. Matching on the declaration alone picks that element, and an observer
 * rooted there sees every descendant at once — the deferral compiles, runs, and does
 * nothing. Mirrors `resolveScroller` in `CenteredCardContext`, plus that check.
 */
function nearestScrollableAncestor(el: Element): Element | null {
  for (let n = el.parentElement; n; n = n.parentElement) {
    const { overflowY } = getComputedStyle(n);
    if ((overflowY === 'auto' || overflowY === 'scroll') && n.scrollHeight > n.clientHeight) {
      return n;
    }
  }
  return null;
}

/**
 * True once the element first nears its root, then stays true (one-shot).
 * Used to defer data fetching and frame capture until content is actually
 * about to be seen — long lists over per-media CDN files would otherwise
 * stampede requests for rows nobody scrolled to.
 *
 * The latch is deliberately sticky. Unloading on exit would release the
 * `ImageCacheService` refcount and re-fetch on re-entry, and that cache has no
 * in-flight dedup, so a fast scroll would fire duplicate requests for the same
 * asset. Sticky also keeps acquire/release balanced — see
 * `.claude/rules/ref-counting-caches.md`.
 */
export function useInView<T extends Element>(
  ref: RefObject<T | null>,
  { rootMargin = '300px', eager = false, root, enabled = true }: UseInViewOptions = {},
): boolean {
  // `eager && enabled`, not `eager`: the latch is one-shot, so starting it latched would put
  // it permanently beyond `enabled`'s reach — silently reinstating the eager fetch that
  // `enabled` exists to prevent, for any caller that combines the two.
  const [inView, setInView] = useState(eager && enabled);

  useEffect(() => {
    if (inView || !enabled) return;
    // `eager` and a missing IntersectionObserver both mean "don't wait to be seen", and both
    // apply the moment `enabled` allows it. Neither needs the element, so both are checked
    // before reading the ref.
    if (eager || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const el = ref.current;
    if (!el) {
      // Terminal, not a retry: every dependency here is a stable identity, so nothing will
      // re-run this effect once the ref fills in, and the latch stays false for the
      // component's life — the content never loads and nothing says why. Every caller today
      // attaches the ref to an unconditionally-rendered root, so it cannot happen; a caller
      // that attaches it inside a conditional branch would hit it. Wants a callback ref to
      // fix properly, which is an API change for all five callers.
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          '[useInView] the observed ref is empty at effect time, so nothing will ever be observed. Attach the ref to an element that always renders.',
        );
      }
      return;
    }

    const rootEl =
      root === 'nearest-scrollable' ? nearestScrollableAncestor(el) : (root?.current ?? null);

    // Every way of getting the root wrong is silent, and they fail in opposite directions: a
    // root that isn't an ancestor never intersects and nothing loads, while one that isn't
    // scrollable contains everything at once and the deferral quietly becomes a no-op.
    // Neither raises an error, and the second is invisible even in the network tab unless you
    // count requests against rows — it shipped past a review and three rounds of manual
    // testing here before anyone caught it.
    if (process.env.NODE_ENV !== 'production') {
      if (rootEl && !rootEl.contains(el)) {
        console.warn(
          '[useInView] the root is not an ancestor of the observed element — this observer can never fire.',
          { root: rootEl, element: el },
        );
      } else if (rootEl && rootEl.scrollHeight <= rootEl.clientHeight + 1) {
        console.warn(
          '[useInView] the root is not scrollable, so every target already intersects and the deferral is doing nothing. Harmless when the content genuinely fits; if clientHeight is roughly the full content height, the wrong element is being used as the root.',
          { root: rootEl, clientHeight: rootEl.clientHeight, scrollHeight: rootEl.scrollHeight },
        );
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { root: rootEl, rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, enabled, eager, ref, root, rootMargin]);

  return inView;
}
